import type { FC, ChangeEvent } from 'react'
import { useState } from 'react'

import { Input, Popup } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useMutation } from '@tanstack/react-query'

import { queryClient } from '@/app/queryClient'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { LocalDate } from '@/components/LocalDate'
import queries from '@/modules/admin/queries'
import type { AssetDownPayment } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

export const DownPaymentPopup: FC<{
  downPayments: AssetDownPayment[]
  assetId: number
  isOpen: boolean
  onClose: () => void
}> = ({ assetId, downPayments, isOpen, onClose }) => {
  const translate = useTranslation()

  return (
    <Popup
      isOpen={isOpen}
      variant="narrow"
      ariaLabel={translate('Edit payment amount')}
      onClose={onClose}
    >
      <h2 className="mb-2">
        <Translation id="Edit down payment" />
      </h2>

      {downPayments.map(downPayment => (
        <DownPaymentEditOptions
          key={downPayment.id}
          assetId={assetId}
          downPayment={downPayment}
          onClose={onClose}
        />
      ))}
    </Popup>
  )
}

const DownPaymentEditOptions: FC<{
  downPayment: AssetDownPayment
  assetId: number
  onClose: () => void
}> = ({ downPayment, assetId, onClose }) => {
  const [editedPaymentAmount, setEditedPaymentAmount] = useState(downPayment.amount)

  const handleChangePaymentAmount = (event: ChangeEvent<HTMLInputElement>) => {
    const amount = parseFloat(event.target.value)
    setEditedPaymentAmount(amount)
  }

  const editDownPaymentMutation = useMutation(
    () =>
      adminResources.addDownPayment({
        amount: editedPaymentAmount,
        downPaymentId: downPayment.id,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queries.adminAssetList())
        queryClient.invalidateQueries(queries.adminAsset(assetId))
      },
    }
  )

  const deleteDownPaymentMutation = useMutation(
    () => adminResources.deleteDownPayment({ downPaymentId: downPayment.id }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queries.adminAssetList())
        queryClient.invalidateQueries(queries.adminAsset(assetId))
        onClose()
      },
    }
  )
  return (
    <div className="flex items-baseline justify-end">
      <div className="mr-8 text-lg">
        <LocalDate date={downPayment.date} />
      </div>

      <Input
        isFullWidth
        isDisabled={!downPayment.isActive}
        marginRight={8}
        type="number"
        value={editedPaymentAmount}
        onChange={handleChangePaymentAmount}
        id="paymentAmount"
      />

      <Button
        isLoading={deleteDownPaymentMutation.isLoading}
        disabled={!downPayment.isActive}
        onClick={() => deleteDownPaymentMutation.mutate()}
      >
        <Translation id="Delete" />
      </Button>
      <Button
        variant="primary"
        isLoading={editDownPaymentMutation.isLoading}
        disabled={!downPayment.isActive}
        onClick={() => editDownPaymentMutation.mutate()}
      >
        <Translation id="Save" />
      </Button>
    </div>
  )
}
